import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface AlibabaWanx22I2vPlusParams {
  /**
   * 输入参数
   */
  input: {
    /**
     * 图像URL，I2V模型必需，支持Base64编码或HTTP/HTTPS URL
     */
    img_url: string;
    /**
     * 文本提示词，描述要生成的视频内容，不超过800字符
     * @length 0 - 800
     */
    prompt?: string;
  };
  /**
   * 可选参数
   */
  parameters?: {
    /**
     * 视频分辨率，支持 '480P' 或 '1080P'
     * @default "1080P"
     * @values 480P | 1080P
     */
    resolution?: '480P' | '1080P';
    /**
     * 视频时长，固定为5秒，不可修改
     * @default 5
     */
    duration?: number;
    /**
     * 是否启用提示词扩展
     * @default true
     */
    prompt_extend?: boolean;
    /**
     * 是否添加水印
     * @default false
     */
    watermark?: boolean;
    /**
     * 随机种子，用于可重现的生成结果，范围0-2147483647
     * @range 0 - 2147483647
     */
    seed?: number;
    /**
     * 负面提示词，描述不希望出现在视频中的内容，不超过500字符
     */
    negative_prompt?: string;
  };
}

export interface AlibabaWanx22I2vPlusResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * alibaba_wanx22_i2v_plus
 *
 * 阿里巴巴 Wanx2.2 I2V Plus 图像生成视频实现。
 * 
 * Model: wan2.2-i2v-plus
 * Features: 图像生成视频 (Image-to-Video)
 * Resolution: 480P, 1080P (默认1080P)
 * Duration: 固定5秒
 * 
 * API Documentation: https://help.aliyun.com/zh/model-studio/image-to-video-api-reference
 *
 * @example
 * ```typescript
 * import { initSeacloud, alibabaWanx22I2vPlus } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await alibabaWanx22I2vPlus({
 *   // 参数见 AlibabaWanx22I2vPlusParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 AlibabaWanx22I2vPlusParams 接口定义
 * @returns 生成的资源列表
 */
export async function alibabaWanx22I2vPlus(
  params: AlibabaWanx22I2vPlusParams,
): Promise<AlibabaWanx22I2vPlusResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'alibaba_wanx22_i2v_plus',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: AlibabaWanx22I2vPlusResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
